import {
  CaretDown,
  CaretUp,
  ChartBar,
  Check,
  Funnel,
  Gear,
  Info,
  Megaphone,
  SlidersHorizontal,
  X,
} from "@phosphor-icons/react";
import { ColumnDef } from "@tanstack/react-table";
import { Badge, Button, Tooltip } from "flowbite-react";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AnimatedModal from "../../../../components/AnimatedModal";
import {
  BannerHeightOffset,
  BannerNote,
} from "../../../../components/BannerNote";
import CountryDisplay from "../../../../components/CountryDisplay";
import CustomSwitch from "../../../../components/CustomSwitch";
import DateRangePickerButton from "../../../../components/DateRangePicker/DateRangePickerButton";
import Drawer from "../../../../components/Drawer";
import SearchInput from "../../../../components/SearchInput";
import { Table } from "../../../../components/Table";
import { useDateRange } from "../../../../contexts/DateRangeContext";
import {
  useAppDetailsData,
  useAuth,
  useCampaignCountryCost,
  useCountryChartData,
  useGrowthIndicator,
  useIsMobile,
} from "../../../../hooks";
import { IDateRangePreset } from "../../../../interfaces";
import { checkPermission, formatNumber, getNetworkWiseRevenue } from "../../../../lib/utils";
import {
  CAMPAIGN_STATUS,
  DEFAULT_ITEMS_PER_PAGE,
  DEMO_ACCOUNTS,
  PERMISSION_MAPPER,
} from "../../../../shared/constants";
import AppLogo from "../../../AppCountryReports/components/AppLogo";
import { IBidEntry } from "../../../AppCountryReports/interface";
import AppRevenueChart from "../../components/AppRevenueChart";
import CampaignCountryCostModal from "../../components/CampaignCountryCostModal";
import { IReportDetailsEntity } from "../../interface";
import AppDetailsHeaderSkeleton from "./AppDetailsSkeleton";

const INITIAL_SELECTED_COUNTRY_COST_MODAL_STATE = {
  open: false,
  loading: false,
  countryCode: "",
  countryName: "",
  data: undefined as
    | undefined
    | {
      costs: { campaign_name: string; cost_inr: number }[];
      total_cost_inr: number;
    },
};

const columnIds = {
  COUNTRY: "country",
  ESTIMATED_REVENUE: "country_wise_revenue",
  IN_APP_PURCHASES_OR_SUBSCRIPTIONS: "developer_proceeds",
  COST_DOLLAR: "without_tax.campaign_cost_usd",
  COST_INR: "without_tax.campaign_cost_inr",
  TOTAL_COST_DOLLAR: "without_tax.total_campaign_cost_usd",
  TOTAL_COST_INR: "without_tax.total_campaign_cost_inr",
  NET_DOLLAR: "without_tax.total_net",
  PERCENTAGE: "without_tax.net_percentage",
  LAST_BID_CURRENT_BID_RUPEE: "bid",
  COST_DOLLAR_WITH_TAX: "with_tax.campaign_cost_usd",
  COST_INR_WITH_TAX: "with_tax.campaign_cost_inr",
  TOTAL_COST_DOLLAR_WITH_TAX: "with_tax.total_campaign_cost_usd",
  TOTAL_COST_INR_WITH_TAX: "with_tax.total_campaign_cost_inr",
  NET_DOLLAR_WITH_TAX: "with_tax.total_net",
  PERCENTAGE_WITH_TAX: "with_tax.net_percentage",
  ACTIONS: "actions",

  UNIVERSAL_LEVEL_COST_DOLLAR: "without_tax.universal_level_cost_usd",
  UNIVERSAL_LEVEL_COST_INR: "without_tax.universal_level_cost_inr",
  UNIVERSAL_LEVEL_COST_DOLLAR_WITH_TAX: "with_tax.universal_level_cost_usd",
  UNIVERSAL_LEVEL_COST_INR_WITH_TAX: "with_tax.universal_level_cost_inr",
};

const withTaxColumnIds = [
  columnIds.COST_DOLLAR_WITH_TAX,
  columnIds.COST_INR_WITH_TAX,
  columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
  columnIds.TOTAL_COST_INR_WITH_TAX,
  columnIds.NET_DOLLAR_WITH_TAX,
  columnIds.PERCENTAGE_WITH_TAX,
];

const UniversalValueTooltip: React.FC<{ value: number, universalValue: number }> = ({ value, universalValue }) => {
  const { t } = useTranslation();
  return (
    <div>
      <span className="text-sm whitespace-nowrap text-right block">
        {isNaN(value) ? "0.00" : formatNumber(value)}
      </span>
      <div className="relative flex items-center justify-end">
        <Tooltip content={t("common.universalValueTooltip")}>
          <span className="text-sm whitespace-nowrapblock text-gray-900">
            {isNaN(universalValue) ? "0.00" : formatNumber(universalValue)}
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

const BidDataCell = ({
  bidData,
  t,
}: {
  bidData: IBidEntry[];
  t: (key: string) => string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!bidData || bidData.length === 0) {
    return null;
  }

  const maxVisibleEntries = 2;
  const visibleBids = bidData.slice(0, maxVisibleEntries);
  const remainingCount = bidData.length - maxVisibleEntries;

  const renderBidEntry = (bid: IBidEntry, index: number) => {
    if (bid.campaign_type === "TARGET_CPA") {
      const oldBid = bid.old_bid.toFixed(2);
      const currentBid = bid.current_bid.toFixed(2);
      const textColor =
        bid.campaign_status === CAMPAIGN_STATUS.ENABLED
          ? "text-gray-900"
          : "text-gray-500";

      if (oldBid === currentBid) {
        return (
          <div key={index} className={`${textColor} flex items-center gap-1`}>
            {currentBid}
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        );
      }

      return (
        <div key={index} className={`flex items-center gap-1`}>
          <div className={textColor}>{oldBid}</div>
          <div
            className={
              bid.current_bid > bid.old_bid ? "text-green-500" : "text-red-500"
            }
          >
            {bid.current_bid > bid.old_bid ? "↑" : "↓"}
          </div>
          <div className={`font-medium ${textColor} flex items-center gap-1`}>
            {currentBid}
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        </div>
      );
    } else if (
      bid.campaign_type === "TARGET_ROAS" &&
      bid.troas !== undefined &&
      bid.old_troas !== undefined
    ) {
      const textColor =
        bid.campaign_status === CAMPAIGN_STATUS.ENABLED
          ? "text-gray-900"
          : "text-gray-500";

      if (bid.troas === bid.old_troas) {
        return (
          <div key={index} className={`${textColor} flex items-center gap-1`}>
            {bid.troas}%
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        );
      }

      return (
        <div key={index} className={`flex items-center gap-1`}>
          <div className={textColor}>{bid.old_troas}%</div>
          <div
            className={
              bid.troas > bid.old_troas ? "text-green-500" : "text-red-500"
            }
          >
            {bid.troas > bid.old_troas ? "↑" : "↓"}
          </div>
          <div className={`font-medium ${textColor} flex items-center gap-1`}>
            {bid.troas}%
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col space-y-2">
      {/* Always show first 3 entries */}
      {visibleBids.map((bid: IBidEntry, index: number) =>
        renderBidEntry(bid, index)
      )}
      {/* Show expanded entries when expanded, with animation */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            key="expanded-bids"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
              height: { duration: 0.3, ease: "easeInOut" },
              opacity: { duration: 0.2, ease: "easeInOut" },
            }}
            className="overflow-hidden"
          >
            <div className="space-y-2">
              {bidData
                .slice(maxVisibleEntries)
                .map((bid: IBidEntry, index: number) =>
                  renderBidEntry(bid, index + maxVisibleEntries)
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Expandable indicator */}
      {remainingCount > 0 && (
        <div
          className="text-xs text-gray-500 font-medium flex items-center gap-1 cursor-pointer hover:text-gray-700 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </motion.div>
          {isExpanded
            ? t("common.showLess")
            : `+${remainingCount} ${t("common.more")}`}
        </div>
      )}
    </div>
  );
};

const AppDetail: React.FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, updatePreferences } = useAuth();
  const { dateRange, setDateRange } = useDateRange();
  const [searchValue, setSearchValue] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<IDateRangePreset | null>(
    null
  );
  const [pathName, setPathName] = useState<string>("");
  const location = useLocation();
  const currentPath = location?.pathname.split("/");
  const account = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("account") || "";
  }, [location.search]);

  useEffect(() => {
    if (currentPath[1] === "apps") {
      setPathName("apps");
    } else {
      setPathName("app-country-reports");
    }
  }, [currentPath]);

  const [selectedPresetForChart, setSelectedPresetForChart] =
    useState<IDateRangePreset | null>(null);
  // const [withTax, setWithTax] = useState(false);

  const appDetailPreference = useMemo(
    () => user?.preferences?.app_detail,
    [user]
  );
  const columnConfig = useMemo(
    () => appDetailPreference?.column_config ?? {},
    [appDetailPreference]
  );

  const showWithTax = useMemo(
    () =>
      Object.keys(columnConfig).length > 0
        ? Object.entries(columnConfig)
          .filter(([key]) => withTaxColumnIds.includes(key))
          .every(([key]) => columnConfig[key] === true)
        : false,
    [columnConfig]
  );

  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    code: string;
  } | null>(null);
  const [countryCostModal, setCountryCostModal] = useState(
    INITIAL_SELECTED_COUNTRY_COST_MODAL_STATE
  );
  const [columnOptions, setColumnOptions] = useState([
    {
      id: columnIds.COUNTRY,
      label: t("common.columns.country"),
      enabled: columnConfig[columnIds.COUNTRY] ?? true,
    },
    {
      id: columnIds.ESTIMATED_REVENUE,
      label: t("common.columns.estimatedRevenueDollar"),
      enabled: columnConfig[columnIds.ESTIMATED_REVENUE] ?? true,
    },
    {
      id: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
      label: t("common.columns.inAppPurchasesOrSubscriptions"),
      enabled:
        columnConfig[columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS] ?? true,
    },
    {
      id: columnIds.COST_DOLLAR,
      label: t("common.columns.costDollar"),
      enabled: columnConfig[columnIds.COST_DOLLAR] ?? true,
    },
    {
      id: columnIds.COST_INR,
      label: t("common.columns.costRupee"),
      enabled: columnConfig[columnIds.COST_INR] ?? true,
    },
    {
      id: columnIds.TOTAL_COST_DOLLAR,
      label: t("common.columns.totalCostDollar"),
      enabled: columnConfig[columnIds.TOTAL_COST_DOLLAR] ?? true,
    },
    {
      id: columnIds.TOTAL_COST_INR,
      label: t("common.columns.totalCostRupee"),
      enabled: columnConfig[columnIds.TOTAL_COST_INR] ?? true,
    },
    {
      id: columnIds.NET_DOLLAR,
      label: t("common.columns.netDollar"),
      enabled: columnConfig[columnIds.NET_DOLLAR] ?? true,
    },
    {
      id: columnIds.PERCENTAGE,
      label: t("common.columns.percentage"),
      enabled: columnConfig[columnIds.PERCENTAGE] ?? true,
    },
    {
      id: columnIds.LAST_BID_CURRENT_BID_RUPEE,
      label: t("common.columns.lastBidCurrentBidRupee"),
      enabled: columnConfig[columnIds.LAST_BID_CURRENT_BID_RUPEE] ?? true,
    },
    {
      id: columnIds.COST_DOLLAR_WITH_TAX,
      label: t("common.columns.costDollarWithTax"),
      enabled: columnConfig[columnIds.COST_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.COST_INR_WITH_TAX,
      label: t("common.columns.costRupeeWithTax"),
      enabled: columnConfig[columnIds.COST_INR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
      label: t("common.columns.totalCostDollarWithTax"),
      enabled: columnConfig[columnIds.TOTAL_COST_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.TOTAL_COST_INR_WITH_TAX,
      label: t("common.columns.totalCostRupeeWithTax"),
      enabled: columnConfig[columnIds.TOTAL_COST_INR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.NET_DOLLAR_WITH_TAX,
      label: t("common.columns.netDollarWithTax"),
      enabled: columnConfig[columnIds.NET_DOLLAR_WITH_TAX] ?? false,
    },
    {
      id: columnIds.PERCENTAGE_WITH_TAX,
      label: t("common.columns.percentageWithTax"),
      enabled: columnConfig[columnIds.PERCENTAGE_WITH_TAX] ?? false,
    },
    {
      id: columnIds.ACTIONS,
      label: t("apps.columns.actions"),
      enabled: columnConfig[columnIds.ACTIONS] ?? true,
    },
  ]);

  // Use React Query hooks
  const {
    data: appDetailsData,
    isLoading,
    refetch: fetchAppDetails,
    error,
  } = useAppDetailsData({
    slug: slug || "",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const {
    data: campaignCountryCostData,
    isLoading: isLoadingCampaignCountryCost,
  } = useCampaignCountryCost({
    appPackageId: appDetailsData?.results.app_details.app_package_id || "",
    countryCode: selectedCountry?.code || "",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const { data: chartData, isLoading: isLoadingChartData } =
    useCountryChartData({
      appSlug: slug || "",
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      countryCode: selectedCountry?.code || "",
    });

  // Fetch growth indicator settings
  const { data: growthIndicatorData } = useGrowthIndicator();
  const growthIndicatorRange = useMemo(
    () => growthIndicatorData?.growth_indicators || [0, 50],
    [growthIndicatorData]
  );

  // Check permissions
  const hasCampaignBidReadPermission = useMemo(
    () =>
      checkPermission(
        PERMISSION_MAPPER.BID_MANAGEMENT_READ,
        "modules",
        user?.user_permissions
      ),
    [user?.user_permissions]
  );

  const hasChartReadPermission = useMemo(
    () =>
      checkPermission(
        PERMISSION_MAPPER.APP_DETAIL_CHART_READ,
        "modules",
        user?.user_permissions
      ),
    [user?.user_permissions]
  );

  const hasCountryCostReadPermission = useMemo(
    () =>
      checkPermission(
        PERMISSION_MAPPER.CAMPAIGN_COUNTRY_COST_READ,
        "modules",
        user?.user_permissions
      ),
    [user?.user_permissions]
  );

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  // Extract data from the query result
  const appDetails = useMemo(
    () => appDetailsData?.results?.app_details,
    [appDetailsData]
  );
  const reportDetails = useMemo(
    () => appDetailsData?.results?.report_details || [],
    [appDetailsData]
  );

  // Filter report details based on search value
  const filteredReportDetails = useMemo(() => {
    let filtered = [...reportDetails];
    if (searchValue) {
      filtered = filtered.filter((app) => {
        const searchLower = searchValue.toLowerCase();
        return (
          app.country.toLowerCase().includes(searchLower) ||
          app.country_code.toLowerCase().includes(searchLower) ||
          String(app.country_wise_revenue)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.campaign_cost_usd).toLowerCase().includes(searchLower) ||
          String(app.campaign_cost_inr).toLowerCase().includes(searchLower) ||
          String(app.total_campaign_cost_usd)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.total_campaign_cost_inr)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.total_net).toLowerCase().includes(searchLower) ||
          String(app.with_tax.total_campaign_cost_inr)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.with_tax.total_campaign_cost_usd)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.with_tax.total_net).toLowerCase().includes(searchLower) ||
          String(app.with_tax.net_percentage)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.without_tax.total_campaign_cost_inr)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.without_tax.total_campaign_cost_usd)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.without_tax.total_net)
            .toLowerCase()
            .includes(searchLower) ||
          String(app.without_tax.net_percentage)
            .toLowerCase()
            .includes(searchLower)
        );
      });
    }
    return filtered;
  }, [searchValue, reportDetails]);

  const handleColumnToggle = (columnIds: string[], checked?: boolean) => {
    const clonedColumnConfig = [...columnOptions];
    for (let i = 0; i < clonedColumnConfig.length; i++) {
      if (columnIds.includes(clonedColumnConfig[i].id)) {
        clonedColumnConfig[i].enabled =
          checked !== undefined ? checked : !clonedColumnConfig[i].enabled;
      }
    }
    setColumnOptions(clonedColumnConfig);

    updatePreferences({
      app_detail: {
        ...appDetailPreference,
        column_config: clonedColumnConfig.reduce((acc, col) => {
          acc[col.id] = col.enabled;
          return acc;
        }, {} as Record<string, boolean>),
      },
    });
  };

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
    setSelectedPreset(null);
    setSelectedPresetForChart(null);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    if (!preset) return;
    setSelectedPreset(preset);
    const [startDate, endDate] = preset.getRange();
    setDateRange(startDate, endDate);
  };

  const handleChartPresetChange = (preset: IDateRangePreset | undefined) => {
    if (!preset) return;
    setSelectedPresetForChart(preset);
    const [startDate, endDate] = preset.getRange();
    setDateRange(startDate, endDate);
  };

  const handleCountryClick = useCallback(
    (countryCode: string, countryName: string) => {
      setSelectedCountry({ code: countryCode, name: countryName });
      setCountryCostModal({
        ...INITIAL_SELECTED_COUNTRY_COST_MODAL_STATE,
        open: true,
        countryCode,
        countryName,
      });
    },
    []
  );

  const handleBidClick = useCallback(
    (countryCode: string) => {
      try {
        navigate(
          `/${pathName}/${slug}/bid-management?country-code=${countryCode}`
        );
      } catch (error) {
        console.error("Error encoding bid data:", error);
        toast.error("Error opening bid management");
      }
    },
    [navigate, pathName, slug]
  );

  const handleCloseModal = () => {
    setCountryCostModal(INITIAL_SELECTED_COUNTRY_COST_MODAL_STATE);
    setSelectedCountry(null);
  };

  const handleChartClick = useCallback(
    (country: { name: string; code: string }) => {
      setShowChartModal(true);
      setSelectedCountry(country);
    },
    []
  );

  const columns = useMemo<ColumnDef<IReportDetailsEntity>[]>(
    () => [
      {
        accessorKey: columnIds.COUNTRY,
        id: columnIds.COUNTRY,
        size: 180,
        header: t("common.columns.country"),
        cell: ({ row }) => (
          <CountryDisplay
            countryCode={row.original.country_code}
            countryName={row.original.country}
            onClick={
              hasCountryCostReadPermission
                ? () =>
                  handleCountryClick(
                    row.original.country_code,
                    row.original.country
                  )
                : undefined
            }
          />
        ),
      },
      {
        accessorKey: columnIds.ESTIMATED_REVENUE,
        id: columnIds.ESTIMATED_REVENUE,
        header: t("common.columns.estimatedRevenueDollar"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.country_wise_revenue;
          const networkBifurcation = row.original.network_bifurcation;
          const entries = getNetworkWiseRevenue(networkBifurcation || {});
          const hasNetworkBifurcation =
            !!networkBifurcation && Object.keys(networkBifurcation || {}).length > 0 && entries.length >= 2;
          return (
            <div className="w-full flex items-center justify-end relative">
              {hasNetworkBifurcation ? (
                <Tooltip
                  arrow={true}
                  placement="auto"
                  content={
                    <div className="flex flex-col gap-2 min-w-56">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-6">
                          <span className="text-xs font-semibold">Network:</span>
                          <span className="text-xs tabular-nums text-emerald-400 font-semibold">
                            {Number.isFinite(Number(entries?.[0]?.amount))
                              ? formatNumber(Number(entries?.[0]?.amount))
                              : "0.00"}
                          </span>
                        </div>

                        {entries.slice(1).length > 0 ? (
                          <div className="pl-3 border-l-2 border-emerald-500 flex flex-col gap-1">
                            {entries.slice(1).map((e) => (
                              <div
                                key={e.network}
                                className="flex items-center justify-between gap-6"
                              >
                                <span className="text-xs">
                                  {String(e.network).endsWith(":")
                                    ? String(e.network).slice(0, -1)
                                    : String(e.network)}
                                </span>
                                <span className="text-xs tabular-nums">
                                  {Number.isFinite(Number(e.amount))
                                    ? formatNumber(Number(e.amount))
                                    : "0.00"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  }
                >
                  <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
                    <span className="text-sm whitespace-nowrap text-right block">
                      {isNaN(value) ? "0.00" : formatNumber(value)}
                    </span>
                    <span className="inline-flex w-[14px] justify-end">
                      <Info
                        size={14}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
                  <span className="text-sm whitespace-nowrap text-right block">
                    {isNaN(value) ? "0.00" : formatNumber(value)}
                  </span>
                  <span className="inline-flex w-[14px] justify-end invisible">
                    <Info size={14} className="text-gray-400" />
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
        accessorKey: "developer_proceeds",
        header: () => (
          <div className="flex items-center justify-end gap-1 relative">
            <span>{t("apps.columns.inAppPurchasesOrSubscriptions")}</span>
            <Tooltip content="This is Net Amount">
              <Info size={14} className="text-gray-400 hover:text-gray-600 cursor-help" />
            </Tooltip>
          </div>
        ),
        size: 170,
        meta: { headerAlign: "right" },
        cell: ({ row }) => {
          const value = row.original.developer_proceeds;
          return (
            <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        accessorKey: columnIds.COST_DOLLAR,
        id: columnIds.COST_DOLLAR,
        header: t("common.columns.costDollar"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.without_tax.campaign_cost_usd;
          const universalValue =
            row.original.without_tax.universal_level_cost_usd;
          return !universalValue ? (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          ) : (
            <UniversalValueTooltip value={value} universalValue={universalValue} />
          );
        },
      },
      {
        accessorKey: columnIds.COST_INR,
        id: columnIds.COST_INR,
        header: t("common.columns.costRupee"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.without_tax.campaign_cost_inr;
          const universalValue =
            row.original.without_tax.universal_level_cost_inr;
          return !universalValue ? (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          ) : (
            <UniversalValueTooltip value={value} universalValue={universalValue} />
          );
        },
      },
      {
        accessorKey: columnIds.TOTAL_COST_DOLLAR,
        id: columnIds.TOTAL_COST_DOLLAR,
        header: t("common.columns.totalCostDollar"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.without_tax.total_campaign_cost_usd;
          return (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        accessorKey: columnIds.TOTAL_COST_INR,
        id: columnIds.TOTAL_COST_INR,
        header: t("common.columns.totalCostRupee"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.without_tax.total_campaign_cost_inr;
          return (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        accessorKey: columnIds.NET_DOLLAR,
        id: columnIds.NET_DOLLAR,
        header: t("common.columns.netDollar"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.without_tax.total_net;
          return (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        accessorKey: columnIds.PERCENTAGE,
        id: columnIds.PERCENTAGE,
        header: t("common.columns.percentage"),
        size: 110,
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.without_tax.net_percentage;
          let color = "gray";

          const percentageValue = value * 100; // Multiply by 100 before comparison
          const [minThreshold, maxThreshold] = growthIndicatorRange;
          if (percentageValue < minThreshold) {
            color = "failure";
          } else if (percentageValue >= minThreshold && percentageValue <= maxThreshold) {
            color = "warning";
          } else if (percentageValue > maxThreshold) {
            color = "success";
          }

          return (
            <Badge
              color={color}
              size="sm"
              className="whitespace-nowrap ml-auto"
            >
              {isNaN(value) ? "0.00" : formatNumber(value * 100)}%
            </Badge>
          );
        },
      },

      // With Tax columns
      {
        id: columnIds.COST_DOLLAR_WITH_TAX,
        accessorKey: columnIds.COST_DOLLAR_WITH_TAX,
        header: t("common.columns.costDollarWithTax"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.with_tax.campaign_cost_usd;
          const universalValue = row.original.with_tax.universal_level_cost_usd;
          return !universalValue ? (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          ) : (
            <UniversalValueTooltip value={value} universalValue={universalValue} />
          );
        },
      },
      {
        id: columnIds.COST_INR_WITH_TAX,
        accessorKey: columnIds.COST_INR_WITH_TAX,
        header: t("common.columns.costRupeeWithTax"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.with_tax.campaign_cost_inr;
          const universalValue = row.original.with_tax.universal_level_cost_inr;
          return !universalValue ? (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          ) : (
            <UniversalValueTooltip value={value} universalValue={universalValue} />
          );
        },
      },
      {
        id: columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
        accessorKey: columnIds.TOTAL_COST_DOLLAR_WITH_TAX,
        header: t("common.columns.totalCostDollarWithTax"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.with_tax.total_campaign_cost_usd;
          return (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.TOTAL_COST_INR_WITH_TAX,
        accessorKey: columnIds.TOTAL_COST_INR_WITH_TAX,
        header: t("common.columns.totalCostRupeeWithTax"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.with_tax.total_campaign_cost_inr;
          return (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.NET_DOLLAR_WITH_TAX,
        accessorKey: columnIds.NET_DOLLAR_WITH_TAX,
        header: t("common.columns.netDollarWithTax"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.with_tax.total_net;
          return (
            <span className="text-sm whitespace-nowrap text-right block">
              {isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
          );
        },
      },
      {
        id: columnIds.PERCENTAGE_WITH_TAX,
        accessorKey: columnIds.PERCENTAGE_WITH_TAX,
        header: t("common.columns.percentageWithTax"),
        meta: {
          headerAlign: "right",
        },
        cell: ({ row }) => {
          const value = row.original.with_tax.net_percentage;
          let color = "gray";

          const percentageValue = value * 100; // Multiply by 100 before comparison
          const [minThreshold, maxThreshold] = growthIndicatorRange;
          if (percentageValue < minThreshold) {
            color = "failure";
          } else if (percentageValue >= minThreshold && percentageValue <= maxThreshold) {
            color = "warning";
          } else if (percentageValue > maxThreshold) {
            color = "success";
          }

          return (
            <Badge
              color={color}
              size="sm"
              className="whitespace-nowrap ml-auto"
            >
              {isNaN(value) ? "0.00" : formatNumber(value * 100)}%
            </Badge>
          );
        },
      },
      {
        id: columnIds.LAST_BID_CURRENT_BID_RUPEE,
        accessorKey: columnIds.LAST_BID_CURRENT_BID_RUPEE,
        header: t("common.columns.lastBidCurrentBidRupee"),
        size: 150,
        cell: ({ row }) => {
          const bidData = Object.values(row.original.bid_data);

          if (!bidData || bidData.length === 0) {
            return null;
          }

          return <BidDataCell bidData={bidData} t={t} />;
        },
      },
      ...(hasChartReadPermission || hasCampaignBidReadPermission
        ? [
          {
            id: columnIds.ACTIONS,
            header: t("apps.columns.actions"),
            cell: ({ row }) => {
              return (
                <div className="flex gap-2">
                  {hasChartReadPermission && (
                    <Button
                      size="xs"
                      color="blue"
                      outline
                      className="p-[2px]"
                      onClick={() =>
                        handleChartClick({
                          name: row.original.country,
                          code: row.original.country_code,
                        })
                      }
                    >
                      <ChartBar className="h-5" size={16} />
                    </Button>
                  )}
                  {hasCampaignBidReadPermission &&
                    Object.keys(row.original.bid_data).length !== 0 && (
                      <Button
                        size="xs"
                        color="indigo"
                        outline
                        className="p-[2px]"
                        onClick={() =>
                          handleBidClick(row.original.country_code)
                        }
                      >
                        <Megaphone className="h-5" size={16} />
                      </Button>
                    )}
                </div>
              );
            },
          } as ColumnDef<IReportDetailsEntity>,
        ]
        : []),
    ],
    [
      t,
      hasCountryCostReadPermission,
      hasChartReadPermission,
      hasCampaignBidReadPermission,
      handleCountryClick,
      handleChartClick,
      handleBidClick,
      growthIndicatorRange,
    ]
  );

  // Filter columns based on withTax state
  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (col) => columnOptions.find((opt) => opt.id === col.id)?.enabled
      ),
    [columns, columnOptions]
  );

  return (
    <div className="space-y-2">
      {/* Mobile Layout */}
      {isMobile && (
        <div className="flex flex-col gap-4 sm:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isLoading ? (
                <AppDetailsHeaderSkeleton />
              ) : (
                appDetails && (
                  <>
                    <div className="w-12 h-12 rounded-lg">
                      <AppLogo
                        logoUrl={appDetails.app_logo}
                        platform={appDetails.app_platform}
                        appName={appDetails.app_name}
                      />
                    </div>
                    <div>
                      <h1 className="text-xl font-semibold">
                        {appDetails.app_name}
                      </h1>
                      <div className="flex items-center gap-2 mt-1">
                        {account && <Badge color="blue">{account}</Badge>}
                        {appDetails.app_package_id && (
                          <Badge color="indigo">
                            {appDetails.app_package_id}
                          </Badge>
                        )}
                        {appDetails.app_platform && (
                          <Badge
                            color={
                              appDetails.app_platform.toLowerCase() ===
                                "android"
                                ? "green"
                                : "light"
                            }
                          >
                            {appDetails.app_platform}
                          </Badge>
                        )}
                      </div>

                    </div>
                  </>
                )
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <SearchInput
              onSearch={(value: string) => setSearchValue(value)}
              placeholder={t("common.search")}
              className="w-full"
              style={{ height: "38px" }}
              size="md"
            />
            <Button color="indigo" onClick={() => setOpenFilter(true)}>
              <div className="flex items-center gap-2">
                <Funnel size={16} />
                {t("common.filter")}
              </div>
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <CustomSwitch
              checked={showWithTax}
              onChange={(checked) => {
                handleColumnToggle(withTaxColumnIds, checked);
              }}
              label={t("common.withTax")}
            />
            <Button
              size="xs"
              color="light"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="min-w-[32px] h-8"
            >
              <Gear className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>

          <Drawer
            show={openFilter}
            onClose={() => setOpenFilter(false)}
            title={t("appCountryWiseReports.filters.title")}
            size="full"
            footer={
              <>
                <Button
                  color="light"
                  size={"sm"}
                  className="w-full"
                  onClick={() => setOpenFilter(false)}
                >
                  <div className="flex items-center gap-2">
                    <X size={16} />
                    {t("common.close")}
                  </div>
                </Button>
                <Button
                  color="indigo"
                  size={"sm"}
                  className="w-full"
                  onClick={() => {
                    fetchAppDetails();
                    setOpenFilter(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Check size={16} />
                    {t("common.apply")}
                  </div>
                </Button>
              </>
            }
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("common.dateRange")}
                </label>
                <DateRangePickerButton
                  onDateRangeChange={handleDateRangeChange}
                  initialStartDate={dateRange.startDate}
                  initialEndDate={dateRange.endDate}
                  initialPreset={selectedPreset?.id}
                  onPresetChange={handlePresetChange}
                />
              </div>
            </div>
          </Drawer>
        </div>
      )}

      {/* Desktop Layout */}
      {!isMobile && (
        <div className="hidden sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            {isLoading ? (
              <AppDetailsHeaderSkeleton />
            ) : (
              appDetails && (
                <>
                  <div className="w-10 h-10 rounded-lg">
                    <AppLogo
                      logoUrl={appDetails.app_logo}
                      platform={appDetails.app_platform}
                      appName={appDetails.app_name}
                    />
                  </div>
                  <div>
                    <h1 className="text-md font-semibold text-gray-600">
                      {appDetails.app_name}
                    </h1>
                    <div className="flex items-center gap-2">
                      {account !== "null" && (
                        <Badge color="blue" size="xs">
                          {account}
                        </Badge>
                      )}
                      {appDetails.app_package_id && !DEMO_ACCOUNTS.includes(String(user?.schema)) && (
                        <Badge color="indigo" size="xs">
                          {appDetails.app_package_id}
                        </Badge>
                      )}
                      {appDetails.app_platform && (
                        <Badge
                          size="xs"
                          color={
                            appDetails.app_platform.toLowerCase() === "android"
                              ? "green"
                              : "light"
                          }
                        >
                          {appDetails.app_platform}
                        </Badge>
                      )}
                    </div>
                  </div>
                </>
              )
            )}
          </div>
          <div className="flex gap-2 items-center">
            <DateRangePickerButton
              onDateRangeChange={handleDateRangeChange}
              initialStartDate={dateRange.startDate}
              initialEndDate={dateRange.endDate}
              initialPreset={selectedPreset?.id}
              onPresetChange={(preset) => {
                handlePresetChange(preset);
                handleChartPresetChange(preset);
              }}
            />
            <SearchInput
              onSearch={(value: string) => setSearchValue(value)}
              placeholder={t("common.search")}
              className="w-[250px] "
              style={{ height: "38px" }}
              size="md"
            />
            <Button
              size="xs"
              color="light"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="min-w-[32px] h-8"
            >
              <SlidersHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
            <CustomSwitch
              checked={showWithTax}
              onChange={(checked) => {
                handleColumnToggle(withTaxColumnIds, checked);
              }}
              label={t("common.withTax")}
            />
          </div>
        </div>
      )}

      {showColumnSelector && (
        <AnimatedModal
          show={showColumnSelector}
          onClose={() => setShowColumnSelector(false)}
          title={t("apps.columnSelector.title")}
          size="lg"
        >
          <div className="grid grid-cols-2 gap-y-4 gap-x-10 p-4">
            {columnOptions.map((option) => (
              <div
                key={option.id}
                className="flex items-center justify-between"
              >
                <span className="text-sm">{option.label}</span>
                <CustomSwitch
                  checked={option.enabled}
                  onChange={() => handleColumnToggle([option.id])}
                />
              </div>
            ))}
          </div>
        </AnimatedModal>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <Table
          data={filteredReportDetails}
          columns={filteredColumns}
          isLoading={isLoading}
          showPagination={true}
          showSearch={false}
          loadingHeight={3.97}
          loadingRowCount={11}
          pageSize={DEFAULT_ITEMS_PER_PAGE}
          maxHeight={`calc(100vh - ${(isMobile ? 355 : isLoading ? 157 : 215) + BannerHeightOffset
            }px)`}
          isMobile={isMobile}
          summaryKeys={[
            { key: columnIds.COUNTRY, customValue: "SUMMARY" },
            { key: columnIds.ESTIMATED_REVENUE },
            {
              key: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
            },
            {
              key: columnIds.COST_DOLLAR,
              value: [
                columnIds.COST_DOLLAR,
                columnIds.UNIVERSAL_LEVEL_COST_DOLLAR,
              ],
            },
            {
              key: columnIds.COST_INR,
              value: [columnIds.COST_INR, columnIds.UNIVERSAL_LEVEL_COST_INR],
            },
            { key: columnIds.TOTAL_COST_DOLLAR },
            { key: columnIds.TOTAL_COST_INR },
            { key: columnIds.NET_DOLLAR },
            {
              key: columnIds.PERCENTAGE,
              customCalculation: (data: IReportDetailsEntity[]) => {
                const totalRevenue = data.reduce(
                  (sum, row) => sum + (row.country_wise_revenue || 0),
                  0
                );
                const totalNet = data.reduce(
                  (sum, row) => sum + (row.without_tax.total_net || 0),
                  0
                );
                if (totalRevenue === 0) return "0.00%";
                const percentage = (totalNet / totalRevenue) * 100;
                return `${formatNumber(percentage)}%`;
              },
            },
            {
              key: columnIds.COST_DOLLAR_WITH_TAX,
              value: [
                columnIds.COST_DOLLAR_WITH_TAX,
                columnIds.UNIVERSAL_LEVEL_COST_DOLLAR_WITH_TAX,
              ],
            },
            {
              key: columnIds.COST_INR_WITH_TAX,
              value: [
                columnIds.COST_INR_WITH_TAX,
                columnIds.UNIVERSAL_LEVEL_COST_INR_WITH_TAX,
              ],
            },
            { key: columnIds.TOTAL_COST_DOLLAR_WITH_TAX },
            { key: columnIds.TOTAL_COST_INR_WITH_TAX },
            { key: columnIds.NET_DOLLAR_WITH_TAX },
            {
              key: columnIds.PERCENTAGE_WITH_TAX,
              customCalculation: (data: IReportDetailsEntity[]) => {
                const totalRevenue = data.reduce(
                  (sum, row) => sum + (row.country_wise_revenue || 0),
                  0
                );
                const totalNetWithTax = data.reduce(
                  (sum, row) => sum + (row.with_tax.total_net || 0),
                  0
                );
                if (totalRevenue === 0) return "0.00%";
                const percentage = (totalNetWithTax / totalRevenue) * 100;
                return `${formatNumber(percentage)}%`;
              },
            },
          ]}
          summaryPosition="bottom"
          sortByKey={columnIds.ESTIMATED_REVENUE}
          sortOrder="desc"
          leftPinnedColumnIds={[columnIds.COUNTRY]}
        />
      </div>

      <BannerNote
        note={t("common.googleDataNote")}
        title={t("common.dataSourceInfo")}
        marqueeSpeed={25}
        delay={3}
      />

      <CampaignCountryCostModal
        open={countryCostModal.open}
        onClose={handleCloseModal}
        loading={isLoadingCampaignCountryCost}
        data={campaignCountryCostData}
        countryName={countryCostModal.countryName}
      />
      {showChartModal && selectedCountry && (
        <AnimatedModal
          show={showChartModal}
          onClose={() => {
            setSelectedCountry(null);
            setShowChartModal(false);
          }}
          title={t("apps.appRevenueChart.title", {
            country: selectedCountry?.name,
          })}
          size="2xl"
          bodyClassName="p-4"
        >
          <AppRevenueChart
            key={`${selectedPresetForChart?.id}|${JSON.stringify(dateRange)}`}
            data={chartData || []}
            onDateRangeChange={handleDateRangeChange}
            setSelectedPresetForChart={handleChartPresetChange}
            loading={isLoadingChartData}
            dateRange={dateRange}
            selectedPresetForChart={selectedPresetForChart || undefined}
          />
        </AnimatedModal>
      )}
    </div>
  );
};

export default AppDetail;
